import { Component } from '@theme/component';

/**
 * A high-performance custom element that renders a video background with IntersectionObserver lazy-loading and viewport pause/play.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {boolean} */
  #isLoaded = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    if (!videoElement) return;

    // Use IntersectionObserver for viewport-based lazy loading and auto play/pause
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (!this.#isLoaded) {
                this.#loadVideo();
              } else {
                videoElement.play().catch(() => {});
              }
            } else {
              if (this.#isLoaded && !videoElement.paused) {
                videoElement.pause();
              }
            }
          }
        },
        { rootMargin: '300px 0px 300px 0px', threshold: 0.05 }
      );
      this.#observer.observe(this);
    } else {
      this.#loadVideo();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  #loadVideo() {
    if (this.#isLoaded) return;
    this.#isLoaded = true;

    const { videoSources, videoElement } = this.refs;
    if (!videoSources || !videoElement) return;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    videoElement.play().catch(() => {});
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
